<template>
    <DashboardEditSection class="TimeRoundingForm" title="Reports">
        <h4>Automatically round reports</h4>
        <FormToggle
            v-model="roundingEnabled"
            @input="handleToggle"
            title="Automatically round reports"
        />

        <div v-show="roundingEnabled" class="roundingModeForm">
            <h4>Rounding mode</h4>
            <p>Choose how time is rounded in reports.</p>
            <FormSelect v-model="roundingMode">
                <option
                    v-for="option in roundingOptions"
                    :key="option.value"
                    :value="option.value"
                >
                    {{ option.label }}
                </option>
            </FormSelect>

            <small v-if="roundingMode">
                <strong>{{ selectedOptionLabel }}:</strong>
                {{ selectedDefinition }}
                <a :href="learnMoreLink" target="_blank">Learn more</a>
            </small>
        </div>

        <Button class="saveBtn primary" @click="handleSave" :disabled="submitPending || !isSaveEnabled">Save</Button>
    </DashboardEditSection>
</template>

<script>
import { mapState } from 'vuex';
import * as api from '@/api';
import DashboardEditSection from '@/components/DashboardEditSection';
import ChangeTicketCounterModal from '@/components/modals/ChangeTicketCounterModel';
import ConfirmModal from '@/components/modals/ConfirmModal';

export default {
    name: 'TicketCounterForm',

    components: { DashboardEditSection },

    props: {
        company: { type: Object, default: null },
    },

    data: () => ({
        submitPending: false,

        roundingEnabled: null,
        roundingMode: null,
        roundingOptions: [
            { label: '5 minute', value: 5, description: 'Rounds to the nearest 1/12th of an hour.' },
            { label: '6 minute', value: 6, description: 'Rounds to the nearest 1/10th of an hour.' },
            { label: '15 minute', value: 15, description: 'Rounds to the nearest 1/4th of an hour.' },
        ],
        learnMoreLink: 'https://docs.google.com/document/d/119Mf1euwKCvgjpY0F7qq6n_dujsZSCo4jrEPC6CUwqk/edit',
    }),

    computed: {
        ...mapState('company', ['timeRoundingEnabled', 'timeRoundingInterval']),

        selectedOptionLabel() {
            const option = this.roundingOptions.find(o => o.value == this.roundingMode);
            return option ? option.label : '';
        },

        selectedDefinition() {
            const option = this.roundingOptions.find(o => o.value == this.roundingMode);
            return option ? option.description : '';
        },

        isSaveEnabled() {
            if (this.roundingEnabled && !this.roundingMode) {
                return false;
            }

            const currentRoundingEnabled = Boolean(this.roundingEnabled);
            const currentRoundingMode = this.roundingEnabled ? Number(this.roundingMode) : null;

            // Enable only if there are changes
            return (
                this.timeRoundingEnabled !== currentRoundingEnabled ||
                this.timeRoundingInterval !== currentRoundingMode
            );
        },
    },

    created() {
        this.roundingEnabled = this.timeRoundingEnabled;
        this.roundingMode = this.timeRoundingInterval;
    },

    watch: {
        roundingEnabled: {
            handler(newVal) {
                if (!newVal) {
                    this.roundingMode = undefined;
                } else {
                    this.roundingMode = this.timeRoundingInterval;
                }
            },
            immediate: true,
        },
        timeRoundingInterval: {
            handler(newVal) {
                this.roundingMode = newVal;
            },
            immediate: true,
        },
    },

    methods: {
        handleToggle(value) {
            this.roundingEnabled = value;

            if (!value) {
                this.roundingMode = undefined;
            } else {
                this.roundingMode = this.timeRoundingInterval;
            }
        },

        async handleSubmit() {
            if (this.submitPending) return;
            this.submitPending = true;

            const timeRoundingInterval = this.roundingEnabled && this.roundingMode ? Number(this.roundingMode) : null;

            const res = await api.updateCompany(this.company.id, { timeRoundingInterval });
            this.submitPending = false;

            if (res.error) {
                return this.$awn.alert('Failed to update time rounding');
            }

            await this.$store.dispatch('company/fetchOverview');
            this.$awn.success('Time rounding updated successfully');
        },

        handleSave() {
            const message = this.roundingEnabled
                ? `Are you sure you want to enable time rounding to "${this.selectedOptionLabel}"?`
                : 'Are you sure you want to disable time rounding?';

            this.$modal.show(
                ConfirmModal,
                {
                    title: 'Time Rounding',
                    message,
                    type: 'warning',
                    warningMessage: ' Changing time rounding will update all past reports, including tickets from previous projects. This may impact billing or data records.',
                    confirmLabel: 'Update Rounding',
                    onConfirm: this.handleSubmit,
                },
                { height: 'auto' },
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'globals.scss';
.TimeRoundingForm {
    .roundingModeForm {
        margin-top: 30px;
    }

    .saveBtn {
        margin-top: 30px;
        display: block;
        margin-left: auto;
    }

    .FormInputContainer {
        margin-top: 10px;
        margin-bottom: 15px;
    }
}
</style>
